import type { SuperHubStore } from '..';
import type {
  CreateRepresentationType,
  EditRepresentationType,
  ReadRepresentationType,
} from '@readme/api/src/mappings/category/types';
import type { CreateGuideType, ReadGuideType } from '@readme/api/src/mappings/page/guide/types';
import type { CreateReferenceType, ReadReferenceType } from '@readme/api/src/mappings/page/reference/types';
import type { GitSidebarCategory, GitSidebarPage } from '@readme/api/src/mappings/sidebar/types';
import type { WritableDeep } from 'type-fest';
import type { StateCreator } from 'zustand';

import { findSidebarFirstPage as findSidebarFirstPageISO } from '@readme/iso';
import produce from 'immer';

import { mutate } from '@Hub/swr';

import type useReadmeApi from '@core/hooks/useReadmeApi';
import { fetcher } from '@core/hooks/useReadmeApi';
import type { FetcherResponse } from '@core/hooks/useReadmeApi/fetcher';
import { actionLog, isClient } from '@core/store/util';

import type { ItemDropResult } from '@ui/Dash/PageNav/DragDrop';

import { findEntityByURI, getPageParentURI, getCategoryFromURI, SidebarMap, getTotalUnrenderable } from './util';

interface SuperHubSidebarSliceState {
  /**
   * Primary data source for our sidebar.
   */
  data: GitSidebarCategory[];

  /**
   * Contains information about any errors that occurred either during
   * hydration or updates.
   * @todo Still in progress, but we probably want to define our own dedicated
   * shape here as opposed to simply using one passed down from the API server.
   */
  error: unknown;

  /**
   * Store the current sidebar's `etag` header so we can include it when
   * reordering pages or categories to ensure the sidebar has not changed since
   * we last fetched it.
   *
   * If it has, requests should fail with a `412 Precondition Failed` error and
   * the client should re-fetch the latest sidebar and try again.
   *
   * This prevents situations where multiple clients are editing the sidebar
   * at the same time and one client's changes would inadvertently overwrite
   * another's.
   * @link https://developer.mozilla.org/en-US/docs/Web/HTTP/Headers/If-Match
   */
  etag: string | null;

  /**
   * Whether sidebar data is currently being fetched via API.
   */
  isLoading: boolean;

  /**
   * Indicates this slice has been initialized on both server and client. We use
   * this to differentiate how the state should update during SSR vs CSR. On the
   * server, the state should never prevent updates or retain previously
   * existing data. This is only safe to do once this flag is flipped to `true`.
   * @see initialize
   */
  isReady: boolean;

  /**
   * Whether a page reorder operation is currently in progress.
   * Used to visually disable the sidebar and block drag-and-drop operations for pages.
   */
  isReordering: boolean;

  /**
   * Holds reference to the SWR request key that was used to fetch sidebar
   * data from the API. This key can then be used by SWR's `mutate()` function
   * to update cached data to optimistically reflect a changed sidebar while a
   * mutation request is in flight.
   * @link https://swr.vercel.app/docs/mutation
   * @example
   * ```ts
   * '/cats/api-next/v2/branches/1.0.0/sidebar?page_type=guide'
   * ```
   * @example
   * ```ts
   * ['/cats/api-next/v2/branches/1.0.0/sidebar?page_type=guide', {}]
   * ```
   */
  swrKey: ReturnType<typeof useReadmeApi>['swrKey'];

  /**
   * Total number of pages with issues that affect their ability to render content in a normal
   * state, whether it be through having MDX errors or being attached to an invalid API definition.
   */
  totalUnrenderable: {
    /**
     * Total number of pages in the sidebar that has `renderable.status` set to `false`. These pages
     * are unrenderable, typically due to invalid MDX syntax.
     */
    mdx: number;

    /**
     * Total number of API references in the sidebar that have `api_renderable.status` set to
     * `false`. The API definitions that these reference pages are point towards have API
     * validation issues and do not surface any data from that API definition, resulting in an
     * empty API Explorer state.
     */
    apis: number;
  };
}

interface SuperHubSidebarSliceAction {
  /**
   * Creates a new category with the provided title.
   */
  createCategory: (title: string) => Promise<void>;

  /**
   * Deletes a category and removes it from the sidebar.
   */
  deleteCategory: (category: GitSidebarCategory) => Promise<void>;

  /**
   * Deletes a document and removes it from the sidebar.
   */
  deletePage: (page: GitSidebarPage) => Promise<void>;

  /**
   * Creates a duplicate copy of a page document and appends it to the sidebar
   * adjacent to the page item source.
   *
   * Returns the newly created document.
   */
  duplicatePage: (page: GitSidebarPage) => Promise<ReadGuideType['data'] | ReadReferenceType['data']>;

  /**
   * Finds the sidebar category based on the provided `title` with efficient
   * O(1) lookups. Returns `undefined` if no match was found.
   */
  findSidebarCategoryByTitle: (title: string) => GitSidebarCategory | undefined;

  /**
   * Find and return the first page in the sidebar. Excludes `hidden` and `link`
   * pages by default but can be overridden to include them.
   */
  findSidebarFirstPage: (config?: Parameters<typeof findSidebarFirstPageISO>[1]) => GitSidebarPage | null;

  /**
   * Finds the sidebar page based on the provided page `slug` with efficient
   * O(1) lookups. Returns `undefined` if no match was found.
   */
  findSidebarPageBySlug: (slug: string) => GitSidebarPage | undefined;

  /**
   * Updates the sidebar state with data, loading states, error, etc. Call this
   * whenever you need to initialize the sidebar with SSR or incoming API data.
   */
  initialize: (
    payload: Pick<SuperHubSidebarSliceState, 'data' | 'error' | 'isLoading'> & {
      etag?: SuperHubSidebarSliceState['etag'];
      swrKey?: SuperHubSidebarSliceState['swrKey'];
    },
  ) => void;

  /**
   * Updates an existing category with a new title.
   */
  renameCategory: (category: GitSidebarCategory, newTitle: string) => Promise<void>;

  /**
   * Reorders an existing category from its current position to a new position.
   */
  reorderCategory: (title: string, newPosition: number) => Promise<void>;

  /**
   * Reorders an existing page from its current position to a new position.
   */
  reorderPage: (source: ItemDropResult, target: ItemDropResult) => Promise<void>;

  /**
   * Revalidates the sidebar and pulls down the latest data from the API.
   */
  revalidate: () => Promise<GitSidebarCategory[] | undefined>;

  /**
   * Updates our internal cache map based on current state data to enable O(1)
   * lookups. Should be called whenever changes are made to state data.
   */
  updateCache: () => void;
}

export interface SuperHubSidebarSlice {
  /**
   * State slice containing git sidebar data and all actions that can be
   * performed on its categories and page documents.
   */
  sidebar: SuperHubSidebarSliceAction & SuperHubSidebarSliceState;
}

const initialState: SuperHubSidebarSliceState = {
  data: [],
  error: null,
  etag: null,
  isLoading: false,
  isReordering: false,
  isReady: false,
  swrKey: null,
  totalUnrenderable: {
    mdx: 0,
    apis: 0,
  },
};

/**
 * Indexed cache to look up categories or pages in O(1) time. Contains a hash
 * map of either the `category.title` or `page.slug` as keys.
 */
const sidebarMap = new SidebarMap();

/**
 * Contains reference to the previously loaded data. We use this to mimic the
 * `keepPreviousData` SWR option that is not yet available for use in our
 * current SWR version. It allows existing sidebar data to continue showing
 * while revalidation occurs in the background.
 * @link https://swr.vercel.app/docs/advanced/understanding#return-previous-data-for-better-ux
 */
let previousData = initialState.data;

/**
 * SuperHub sidebar state slice containing all things related to sidebar data.
 */
export const createSuperHubSidebarSlice: StateCreator<
  SuperHubSidebarSlice & SuperHubStore,
  [['zustand/devtools', never], ['zustand/immer', never]],
  [],
  SuperHubSidebarSlice
> = (set, get) => ({
  sidebar: {
    ...initialState,

    createCategory: async title => {
      const cleanedTitle = title.trimEnd();
      if (get().sidebar.findSidebarCategoryByTitle(cleanedTitle)) return;

      set(
        state => {
          state.sidebar.data.push({
            pages: [],
            title: cleanedTitle,
            uri: `${cleanedTitle}/PENDING`,
          });
        },
        false,
        actionLog('sidebar.createCategory', cleanedTitle),
      );

      const request = fetcher<ReadRepresentationType>(`${get().getApiBaseUrl()}/categories`, {
        method: 'POST',
        body: JSON.stringify({
          title: cleanedTitle,
          section: get().routeSection === 'reference' ? 'reference' : 'guide',
        } satisfies CreateRepresentationType),
      });

      await mutate<GitSidebarCategory[]>(
        get().sidebar.swrKey,
        request.then(({ data }) =>
          produce(get().sidebar.data, draft => {
            draft[draft.length - 1] = {
              pages: [],
              title: data.title,
              uri: data.uri,
            };
          }),
        ),
        { revalidate: true },
      );

      // Update our index caches to enable 0(1) lookups.
      get().sidebar.updateCache();
    },

    deleteCategory: async category => {
      set(
        state => {
          const indexToRemove = state.sidebar.data.findIndex(c => c.title === category.title);
          state.sidebar.data.splice(indexToRemove, 1);

          // Update total unrenderable count based on the current sidebar.
          state.sidebar.totalUnrenderable = getTotalUnrenderable(state.sidebar.data);
        },
        false,
        actionLog('sidebar.deleteCategory', category),
      );

      const section = get().routeSection === 'reference' ? 'reference' : 'guides';
      const request = fetcher(`${get().getApiBaseUrl()}/categories/${section}/${encodeURIComponent(category.title)}`, {
        method: 'DELETE',
      });

      await mutate<GitSidebarCategory[]>(
        get().sidebar.swrKey,
        request.then(() => get().sidebar.data),
        { revalidate: true },
      );

      // Update our index caches to enable 0(1) lookups.
      get().sidebar.updateCache();
    },

    deletePage: async page => {
      const requestUrl = get().document.getApiUrl({
        slug: page.slug,
        querystring:
          get().routeSection === 'reference'
            ? new URLSearchParams({
                // If we're deleting a reference page then we should also remove the API operation
                // in this page from the API definition it's contained within (assuming that this
                // page has an API operation+definition and that that operation is not in use in
                // another page).
                cleanup_definition: 'true',
              })
            : undefined,
      });
      if (!requestUrl) {
        throw new Error('Missing required request URL');
      }

      set(
        state => {
          const parentURI = getPageParentURI(page.parent || page.category);
          const parentEntity = findEntityByURI(state.sidebar.data, parentURI);
          const parentChildren = parentEntity?.pages;

          const indexToRemove = parentChildren?.findIndex(c => c.slug === page.slug);
          if (indexToRemove !== undefined && indexToRemove !== -1) {
            parentChildren?.splice(indexToRemove, 1);
          }

          // Update total unrenderable count based on the current sidebar.
          state.sidebar.totalUnrenderable = getTotalUnrenderable(state.sidebar.data);
        },
        false,
        actionLog('sidebar.deletePage', page),
      );

      const request = fetcher(requestUrl, {
        method: 'DELETE',
      });

      await mutate<GitSidebarCategory[]>(
        get().sidebar.swrKey,
        request.then(() => get().sidebar.data),
        { revalidate: true },
      );

      // Update our index caches to enable 0(1) lookups.
      get().sidebar.updateCache();
    },

    duplicatePage: async page => {
      const isEndpoint = page.type === 'endpoint';
      const pageUrl = get().document.getApiUrl({ slug: page.slug });
      if (!pageUrl) {
        throw new Error('Missing required request URL');
      }

      // Create pending clone for optimistic update
      const clone = produce(page, draft => {
        draft.pages = [];
        draft.slug += '/PENDING';
        draft.title = `Copy of ${draft.title}`;
        draft.uri += '/PENDING';
      });
      const noParentError = new Error('Cannot find parent entity');

      let clonePosition = 0;
      set(
        state => {
          const parentURI = getPageParentURI(page.parent || page.category);
          const parentEntity = findEntityByURI(state.sidebar.data, parentURI);
          if (!parentEntity) throw noParentError;

          // Update sidebar to include duplicated page adjacent to the source.
          const parentPages = parentEntity.pages;
          clonePosition = parentPages.findIndex(c => c.slug === page.slug) + 1;
          parentPages.splice(clonePosition, 0, clone);

          // Update total unrenderable count based on the current sidebar.
          state.sidebar.totalUnrenderable = getTotalUnrenderable(state.sidebar.data);
        },
        false,
        actionLog('sidebar.duplicatePage', page),
      );

      // Fetch original page so we can then create a duplicate of it.
      const { data: sourceDocument } = await fetcher<ReadGuideType | ReadReferenceType>(pageUrl, {
        method: 'GET',
      });

      let duplicatedData: CreateGuideType | CreateReferenceType;

      if (isEndpoint && 'api' in sourceDocument) {
        // For endpoints, we need to fetch the full definition and create a unique path
        const filename = sourceDocument.api.uri?.split('/apis/')[1];
        if (!filename) throw new Error('Missing required filename');

        const { data: fullDefinition } = await get().apiDefinitions.getDefinition(filename);
        if (!fullDefinition) throw new Error('Could not find file definition');

        // Generate unique path for the duplicated endpoint
        const { path: sourcePath, method: sourceMethod } = sourceDocument.api;
        const sourceOperation = sourceDocument.api.schema.paths[sourcePath][sourceMethod];

        let newPath = sourcePath;
        let pathSuffix = 1;
        while (fullDefinition.schema.paths[newPath]?.[sourceMethod]) {
          newPath = `${sourcePath}-${pathSuffix}`;
          pathSuffix += 1;
        }

        // Use the actual suffix from the path (pathSuffix - 1 after loop increments it)
        const actualSuffix = pathSuffix - 1;

        // Create duplicated data with the new endpoint in the full schema
        duplicatedData = {
          ...sourceDocument,
          title: `Copy of ${sourceDocument.title}`,
          position: clonePosition,
          api: {
            ...sourceDocument.api,
            path: newPath,
            method: sourceMethod,
            schema: {
              ...fullDefinition.schema,
              paths: {
                ...fullDefinition.schema.paths,
                [newPath]: {
                  [sourceMethod]: {
                    ...sourceOperation,
                    operationId:
                      sourceOperation.operationId && actualSuffix > 0
                        ? `${sourceOperation.operationId}-${actualSuffix}`
                        : sourceOperation.operationId,
                    summary: `Copy of ${sourceOperation.summary || ''}`,
                  },
                },
              },
            },
          },
        };
      } else {
        // For non-endpoint pages, use the simple duplication logic
        duplicatedData = produce<CreateGuideType | CreateReferenceType>(sourceDocument, draft => {
          draft.position = clonePosition;
          draft.slug = undefined;
          draft.title = clone.title;
          // If we're duplicating a reference page, we need to ensure we're
          // copying over the API config as well.
          if ('api_config' in draft && typeof clone.api_config !== 'undefined') {
            draft.api_config = clone.api_config;
          }
        });
      }

      // Create the new page via API
      const newPageUrl = get().document.getApiUrl();
      if (!newPageUrl) {
        throw new Error('Missing required request URL');
      }

      const { data: newDocument } = await fetcher<ReadGuideType | ReadReferenceType>(newPageUrl, {
        body: JSON.stringify(duplicatedData),
        method: 'POST',
      });

      await mutate<GitSidebarCategory[]>(
        get().sidebar.swrKey,
        produce(get().sidebar.data, draft => {
          const pageDraft = findEntityByURI(draft, clone.uri);
          if (!pageDraft || !('slug' in pageDraft)) throw noParentError;
          pageDraft.slug = newDocument.slug;
          pageDraft.title = newDocument.title;
          pageDraft.uri = newDocument.uri;
        }),
        { revalidate: true },
      );

      // Update our index caches to enable 0(1) lookups.
      get().sidebar.updateCache();

      return newDocument;
    },

    findSidebarCategoryByTitle: title => {
      return sidebarMap.categories[title.toLowerCase()];
    },

    findSidebarFirstPage: ({ includeHidden = false, includeLinks = false, pageTypes } = {}) => {
      return findSidebarFirstPageISO(get().sidebar.data, {
        includeHidden,
        includeLinks,
        pageTypes,
      });
    },

    findSidebarPageBySlug: slug => {
      return sidebarMap.pages[slug];
    },

    initialize: payload => {
      const { data, error, etag = get().sidebar.etag, isLoading, swrKey } = payload;
      /** Indicates we are connected to an SWR endpoint. */
      const isConnected = !!get().sidebar.swrKey;
      /** Indicates we are updating the currently connected SWR endpoint. */
      const isSameEndpoint = !isConnected || swrKey === get().sidebar.swrKey;
      const nextData = {
        // While sidebar data is revalidating, continue returning existing data
        // until new data replaces it.
        data: isLoading && isSameEndpoint ? previousData : data,
        error,
        etag,
        isLoading,
      };

      // Only continue with a state update if there are changed values. This
      // quiets down the redux devtools action logs to only contain actions
      // that contain differences.
      const hasChanges = Object.entries(nextData).some(([key, value]) => {
        return JSON.stringify(value) !== JSON.stringify(get().sidebar[key]);
      });

      // If the SWR key has changed, we need to update our state even if the
      // data hasn't changed. This ensures we don't miss updates when switching
      // between different sections that have different sidebar endpoints.
      const hasUpdatedKey = swrKey && swrKey !== get().sidebar.swrKey;

      if (!hasChanges && !hasUpdatedKey) return;

      set(
        state => {
          state.sidebar = { ...state.sidebar, ...nextData };

          // When running on the server, we must avoid marking this store as
          // "ready" to ensure it continues receiving updates until it gets
          // initialized on the client's first render.
          state.sidebar.isReady = isClient;

          // Update SWR key that indicates we are connected to an endpoint.
          const writableSwrKey = swrKey as WritableDeep<typeof swrKey>;
          state.sidebar.swrKey = writableSwrKey ?? null;

          // Update total unrenderable count based on the current sidebar.
          state.sidebar.totalUnrenderable = getTotalUnrenderable(state.sidebar.data);
        },
        false,
        actionLog('sidebar.initialize', payload),
      );

      // Update our index caches to enable 0(1) lookups.
      get().sidebar.updateCache();
    },

    renameCategory: async (category, newTitle) => {
      const cleanedTitle = newTitle.trimEnd();
      set(
        state => {
          const target = state.sidebar.data.find(c => c.title === category.title);
          if (target) {
            target.title = cleanedTitle;
          }
        },
        false,
        actionLog('sidebar.renameCategory', { category, newTitle }),
      );

      const section = get().routeSection === 'reference' ? 'reference' : 'guides';
      const request = fetcher<ReadRepresentationType>(
        `${get().getApiBaseUrl()}/categories/${section}/${encodeURIComponent(category.title)}`,
        {
          method: 'PATCH',
          body: JSON.stringify({
            title: cleanedTitle,
          } satisfies EditRepresentationType),
        },
      );

      await mutate<GitSidebarCategory[]>(
        get().sidebar.swrKey,
        request.then(({ data }) =>
          produce(get().sidebar.data, draft => {
            const target = draft?.find(c => c.title === cleanedTitle);
            if (target) {
              target.title = data.title;
              target.uri = data.uri;
            }
          }),
        ),
        { revalidate: true },
      );

      // Update our index caches to enable 0(1) lookups.
      get().sidebar.updateCache();
    },

    reorderCategory: async (uri, newPosition) => {
      const title = getCategoryFromURI(uri)?.title;
      if (!title) {
        throw new Error(`Category title cannot be parsed from the URI: "${uri}"`);
      }

      const currentPosition = get().sidebar.data.findIndex(c => c.title === title);
      if (currentPosition < 0) {
        throw new Error(`Category cannot be found with the provided title: "${title}"`);
      }

      set(
        state => {
          // Remove category from the array in its current position. Note that
          // this mutates the array in-place and will shift the array length.
          const [sourceCategory] = state.sidebar.data.splice(currentPosition, 1);
          // Re-insert source category into its final position.
          state.sidebar.data.splice(newPosition, 0, sourceCategory);
        },
        false,
        actionLog('sidebar.reorderCategory', { uri, currentPosition, newPosition }),
      );

      const section = get().routeSection === 'reference' ? 'reference' : 'guides';
      const request = fetcher<ReadRepresentationType>(`${get().getApiBaseUrl()}/categories/${section}/${title}`, {
        method: 'PATCH',
        body: JSON.stringify({ position: newPosition }),
        // Ensures sidebar hasn't been reordered since we last fetched it.
        headers: { 'If-Match': get().sidebar.etag || '' },
      });

      await mutate<GitSidebarCategory[]>(
        get().sidebar.swrKey,
        request.then((res: FetcherResponse<ReadRepresentationType>) => {
          const newEtag = res?.__headers__?.etag ?? null;
          set(
            state => {
              state.sidebar.etag = newEtag;
            },
            false,
            actionLog('sidebar.etag.update', newEtag),
          );
          return get().sidebar.data;
        }),
        { revalidate: true },
      );
    },

    reorderPage: async (source, target) => {
      const { slug } = findEntityByURI(get().sidebar.data, source.id) as GitSidebarPage;
      const requestUrl = get().document.getApiUrl({ slug });
      if (!requestUrl) {
        throw new Error('Missing required request URL');
      }

      // Set reordering state to visually disable sidebar
      set(
        state => {
          state.sidebar.isReordering = true;
        },
        false,
        actionLog('sidebar.isReordering', true),
      );

      try {
        const categoryChanged = source.categoryId !== target.categoryId;
        const parentChanged = source.parentId !== target.parentId;

        let targetCategory: ReturnType<typeof findEntityByURI>;
        let parent: ReturnType<typeof findEntityByURI>;
        if (categoryChanged || parentChanged) {
          targetCategory = findEntityByURI(get().sidebar.data, target.categoryId!);
          if (!targetCategory) {
            throw new Error(`Category cannot be found with the provided uri: "${target.categoryId}"`);
          }
          parent = findEntityByURI('pages' in targetCategory ? targetCategory.pages : [], target.parentId || '');
        }

        const request = fetcher<ReadRepresentationType>(requestUrl, {
          method: 'PATCH',
          body: JSON.stringify({
            ...(categoryChanged && { category: { uri: targetCategory?.uri } }),
            // When the parent changes, we need to provide the parent and category
            // so we know where the new home for the page is in the event we're
            // completely removing the parent.
            ...(parentChanged && {
              parent: { uri: parent?.uri || null },
              category: { uri: targetCategory?.uri },
            }),
            position: target.position,
          }),
          // Ensures sidebar hasn't been reordered since we last fetched it.
          headers: { 'If-Match': get().sidebar.etag || '' },
        });

        set(
          state => {
            const getSiblings = (entityUri: string) => {
              const parentEntity = findEntityByURI(state.sidebar.data, entityUri);
              const parentChildren = parentEntity?.pages;
              return parentChildren;
            };

            // Remove page from the array in its current position. Note that this
            // mutates the array in-place and will shift the array length.
            const sourceSiblings = getSiblings(source.parentId || source.categoryId || '');
            const indexToRemove = sourceSiblings?.findIndex(c => c.uri === source.id);
            if (indexToRemove === undefined || indexToRemove === -1) return;

            if (!sourceSiblings) return;
            const [sourcePage] = sourceSiblings.splice(indexToRemove, 1);

            // Re-insert source page into its final position.
            const targetSiblings = getSiblings(target.parentId || target.categoryId || '');
            if (!targetSiblings) return;
            targetSiblings.splice(target.position, 0, sourcePage);
          },
          false,
          actionLog('sidebar.reorderPage', { source, target }),
        );

        await mutate(
          get().sidebar.swrKey,
          // Update the local ETag from the PATCH response before revalidating.
          // Otherwise, back-to-back reorders keep sending a stale `If-Match` and can 412.
          request.then((res: FetcherResponse<ReadRepresentationType>) => {
            const newEtag = res?.__headers__?.etag ?? null;
            set(
              state => {
                state.sidebar.etag = newEtag;
              },
              false,
              actionLog('sidebar.etag.update', newEtag),
            );
            return get().sidebar.data;
          }),
          { revalidate: true },
        );
      } finally {
        set(
          state => {
            state.sidebar.isReordering = false;
          },
          false,
          actionLog('sidebar.isReordering', false),
        );
      }
    },

    revalidate: () => {
      return mutate<GitSidebarCategory[]>(get().sidebar.swrKey);
    },

    updateCache: () => {
      // Update our cache for fast lookups.
      sidebarMap.update(get().sidebar.data);

      // Keep reference to our previous data.
      previousData = get().sidebar.data;
    },
  },
});

export * from './ConnectSuperHubSidebarToApi';
export * from './InitializeSuperHubSidebar';
